import React, { useState, useEffect, useCallback, useRef } from 'react';
import AppShell from '../../components/layout/AppShell';
import api from '../../services/api';
import { dashboardApi } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { fmtPKR, fmtNum, fmtDT, fmtDate } from '../../utils/helpers';
import { Spinner, EmptyState, Modal } from '../../components/common';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { RefreshCw, Bell, Clock, Building, AlertTriangle, Check, X, Trash2 } from 'lucide-react';
import {
  BarChart, Bar, Line, XAxis, YAxis, Tooltip,
  ResponsiveContainer, CartesianGrid, AreaChart, Area
} from 'recharts';

// â”€â”€â”€ Shared KPI card â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
function KpiCard({ label, value, sub, icon, color, trend, onClick }) {
  const c = color || 'var(--navy)';
  return (
    <div className="stat-card" style={{ cursor: onClick ? 'pointer' : undefined, borderTop: '3px solid ' + c }}
      onClick={onClick}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
        <div>
          <div className="stat-label">{label}</div>
          <div style={{ fontWeight:700, fontSize:'1.15rem', color:c, marginTop:4 }}>{value || '—'}</div>
          {sub && <div style={{ fontSize:'.72rem', color:'var(--gray-400)', marginTop:3 }}>{sub}</div>}
        </div>
        <div style={{ color:c, opacity:.6, fontSize:'1.3rem' }}>{icon}</div>
      </div>
      {trend !== undefined && (
        <div style={{ marginTop:8, fontSize:'.72rem', color: trend >= 0 ? 'var(--success)' : 'var(--danger)' }}>
          {trend >= 0 ? '▲' : '▼'} {Math.abs(trend)}% vs last period
        </div>
      )}
    </div>
  );
}

// â”€â”€â”€ Branch selector (CPY-05) â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
function BranchSelector({ selected, onSelect }) {
  const [hubs, setHubs] = useState([]);
  useEffect(() => {
    api.get('/admin/hubs').then(r => r.data).then(r => { if (r.success) setHubs(r.data || []); }).catch(() => {});
  }, []);
  return (
    <div style={{ display:'flex', alignItems:'center', gap:6 }}>
      <Building size={14} style={{ color:'var(--gray-400)' }}/>
      <select className="form-control" style={{ width:180, fontSize:'.82rem', padding:'4px 8px' }}
        value={selected} onChange={e => onSelect(e.target.value)}>
        <option value="">All Branches</option>
        {hubs.map(h => <option key={h.hub_id} value={h.hub_id}>{h.name}</option>)}
      </select>
    </div>
  );
}

// â”€â”€â”€ Notification bell (CPY-02) â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
function NotifBell({ onClick, count }) {
  return (
    <button onClick={onClick} style={{ position:'relative', background:'none', border:'1px solid var(--gray-200)', borderRadius:8, padding:'6px 10px', cursor:'pointer', display:'flex', alignItems:'center', gap:6 }}>
      <Bell size={15} style={{ color:'var(--navy)' }}/>
      {count > 0 && (
        <span style={{ position:'absolute', top:-4, right:-4, background:'var(--danger)', color:'white', borderRadius:'50%', width:16, height:16, fontSize:'.6rem', fontWeight:700, display:'flex', alignItems:'center', justifyContent:'center' }}>
          {count > 9 ? '9+' : count}
        </span>
      )}
    </button>
  );
}

// â”€â”€â”€ Single approval card (CPY-03) â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
function ApprovalCard({ item, onApprove, onReject }) {
  const COLORS = { CREDIT_INCREASE:'var(--info)', RATE_CHANGE:'var(--amber)', REFUND:'var(--danger)', DISCOUNT:'var(--warning)', OTHER:'var(--gray-400)' };
  return (
    <div style={{ padding:'12px 16px', borderRadius:8, border:'1px solid var(--gray-200)', marginBottom:8, borderLeft:'4px solid ' + (COLORS[item.approval_type] || 'var(--navy)'), background:'white' }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
        <div style={{ flex:1 }}>
          <div style={{ fontWeight:600, fontSize:'.85rem' }}>{item.approval_type?.replace(/_/g, ' ')}</div>
          <div style={{ fontSize:'.75rem', color:'var(--gray-500)', marginTop:3 }}>
            By {item.requester_name || 'System'} · {fmtDT(item.created_at)}
          </div>
          {item.requested_amount > 0 && (
            <div style={{ fontSize:'.8rem', color:'var(--navy)', marginTop:4, fontWeight:500 }}>
              Amount: {fmtPKR(item.requested_amount)}
            </div>
          )}
          {item.notes && <div style={{ fontSize:'.75rem', color:'var(--gray-500)', marginTop:3, fontStyle:'italic' }}>{item.notes}</div>}
        </div>
        <div style={{ display:'flex', gap:6, flexShrink:0, marginLeft:12 }}>
          <button className="btn btn-success btn-sm" style={{ fontSize:'.7rem' }} onClick={() => onApprove(item.workflow_id)}>
            <Check size={11}/> Approve
          </button>
          <button className="btn btn-danger btn-sm" style={{ fontSize:'.7rem' }} onClick={() => onReject(item.workflow_id)}>
            <X size={11}/> Reject
          </button>
        </div>
      </div>
    </div>
  );
}

// â”€â”€ Role sub-panels â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

function OpsPanel({ data, loading, hubId }) {
  if (loading) return <Spinner />;
  if (!data) return <EmptyState icon="📦" title="No ops data" message="Start scanning or booking shipments." />;
  const getVol = (s) => data.volumes?.find(v => v.status === s)?.count || 0;
  const total = (data.volumes || []).reduce((s, v) => s + parseInt(v.count || 0), 0);
  return (
    <div>
      <div className="stat-grid" style={{ marginBottom:20 }}>
        <KpiCard label="Booked Today"      value={fmtNum(total)}                              color="var(--navy)"   icon="📦"/>
        <KpiCard label="Awaiting Pickup"   value={fmtNum(data.pendingActions?.awaiting_pickup)} color="var(--info)"   icon="📅"/>
        <KpiCard label="At Hub"            value={fmtNum(data.pendingActions?.at_hub)}          color="var(--navy)"   icon="🏭"/>
        <KpiCard label="Out for Delivery"  value={fmtNum(data.pendingActions?.out_for_delivery)}color="var(--amber)"  icon="🛵"/>
        <KpiCard label="Delivered Today"   value={fmtNum(getVol('DELIVERED'))}                  color="var(--success)"icon="✅"/>
        <KpiCard label="Delayed"           value={fmtNum(data.exceptions?.delayed || 0)}        color="var(--danger)" icon="⚠️"/>
      </div>
      {(data.exceptions?.scan_gaps > 0) && (
        <div className="alert alert-warning" style={{ marginBottom:16, display:'flex', gap:10 }}>
          <AlertTriangle size={15} style={{ flexShrink:0, marginTop:2 }}/>
          <span><strong>Scan gaps detected:</strong> {data.exceptions.scan_gaps} shipments with missing scans</span>
        </div>
      )}
      {data.trend?.length > 0 && (
        <div className="card" style={{ marginBottom:16 }}>
          <div className="card-header"><span className="card-title">7-Day Shipment Trend</span></div>
          <div className="card-body">
            <ResponsiveContainer width="100%" height={180}>
              <AreaChart data={data.trend}>
                <defs>
                  <linearGradient id="gOps" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#1B2A4A" stopOpacity={0.2}/>
                    <stop offset="95%" stopColor="#1B2A4A" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0"/>
                <XAxis dataKey="date" tick={{ fontSize:11 }} tickFormatter={d => d ? d.slice(5) : ''}/>
                <YAxis tick={{ fontSize:11 }}/>
                <Tooltip/>
                <Area type="monotone" dataKey="total" name="Total" stroke="#1B2A4A" fill="url(#gOps)" strokeWidth={2}/>
                <Area type="monotone" dataKey="delivered" name="Delivered" stroke="#10B981" fill="none" strokeWidth={2}/>
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:10 }}>
        {[['Scan Terminal','/ops/scan','📷'],['Book Shipment','/ops/booking','📦'],['Manifests','/ops/manifests','📋'],['All Shipments','/ops/shipments','🚚']].map(([l,to,ic]) => (
          <Link key={to} to={to} style={{ textDecoration:'none' }}>
            <div className="stat-card" style={{ textAlign:'center', padding:'12px 8px', cursor:'pointer' }}>
              <div style={{ fontSize:20, marginBottom:4 }}>{ic}</div>
              <div style={{ fontSize:'.75rem', fontWeight:600, color:'var(--navy)' }}>{l}</div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

function FinancePanel() {
  const [d, setD] = useState(null);
  const [aging, setAging] = useState([]);
  const [load, setLoad] = useState(true);
  useEffect(() => {
    Promise.all([
      api.get('/finance/dashboard').then(r => r.data).catch(() => ({ success:false })),
      api.get('/finance/aging').then(r => r.data).catch(() => ({ success:false })),
    ]).then(([dash, ag]) => {
      if (dash.success) setD(dash.data);
      if (ag.success) setAging(ag.data || []);
    }).finally(() => setLoad(false));
  }, []);
  if (load) return <Spinner />;
  const agT = aging.reduce((s,r) => ({ cur:s.cur+parseFloat(r.current_due||0), d30:s.d30+parseFloat(r.overdue_30||0), d60:s.d60+parseFloat(r.overdue_60||0), d90:s.d90+parseFloat(r.overdue_90||0)+parseFloat(r.overdue_120plus||0) }), { cur:0, d30:0, d60:0, d90:0 });
  return (
    <div>
      <div className="stat-grid" style={{ marginBottom:20 }}>
        <KpiCard label="Collected MTD"    value={fmtPKR(d?.invoices?.collected_mtd)} color="var(--success)" icon="💰"/>
        <KpiCard label="Pending Invoices" value={fmtPKR(d?.invoices?.pending)}        color="var(--warning)" icon="📄" sub={(d?.invoices?.pending_count||0)+' invoices'}/>
        <KpiCard label="Overdue"          value={fmtPKR(d?.invoices?.overdue)}         color="var(--danger)"  icon="⚠️"/>
        <KpiCard label="COD Pending"      value={fmtPKR(d?.cod?.find(c=>c.deposit_status==='PENDING')?.total)} color="var(--amber)" icon="💵"/>
      </div>
      <div className="card" style={{ marginBottom:16 }}>
        <div className="card-header">
          <span className="card-title">AR Aging</span>
          <Link to="/finance/aging" className="btn btn-outline btn-sm">Full Report</Link>
        </div>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:8, padding:16 }}>
          {[['Current',agT.cur,'var(--info)'],['1-30 Days',agT.d30,'var(--warning)'],['31-60 Days',agT.d60,'var(--amber)'],['60+ Days',agT.d90,'var(--danger)']].map(([l,v,c]) => (
            <div key={l} style={{ textAlign:'center', padding:'12px 8px', borderRadius:8, background:c.replace('var(--','').replace(')','')+'10', border:'1px solid var(--gray-200)' }}>
              <div style={{ fontSize:'.7rem', color:'var(--gray-500)', marginBottom:4 }}>{l}</div>
              <div style={{ fontWeight:700, color:c, fontSize:'.95rem' }}>{fmtPKR(v)}</div>
            </div>
          ))}
        </div>
      </div>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:10 }}>
        {[['Invoices','/finance/invoices','📄'],['Vouchers','/finance/vouchers','📋'],['Trial Balance','/finance/trial-balance','⚖️'],['P&L','/finance/pnl','📊']].map(([l,to,ic]) => (
          <Link key={to} to={to} style={{ textDecoration:'none' }}>
            <div className="stat-card" style={{ textAlign:'center', padding:'12px 8px', cursor:'pointer' }}>
              <div style={{ fontSize:20, marginBottom:4 }}>{ic}</div>
              <div style={{ fontSize:'.75rem', fontWeight:600, color:'var(--navy)' }}>{l}</div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

function HRPanel() {
  const [d, setD] = useState(null);
  const [load, setLoad] = useState(true);
  useEffect(() => {
    Promise.all([
      api.get('/employees?limit=1').then(r=>r.data).catch(()=>({ success:false })),
      api.get('/hr/attendance/today').then(r=>r.data).catch(()=>({ success:false })),
      api.get('/hr/leave?status=PENDING&limit=5').then(r=>r.data).catch(()=>({ success:false })),
    ]).then(([emp, att, lv]) => {
      setD({ total:emp.success?emp.pagination?.total||0:0, att:att.success?att.data:null, leaves:lv.success?lv.data||[]:[] });
    }).finally(() => setLoad(false));
  }, []);
  if (load) return <Spinner />;
  return (
    <div>
      <div className="stat-grid" style={{ marginBottom:20 }}>
        <KpiCard label="Total Employees" value={fmtNum(d?.total)}              color="var(--navy)"   icon="👥"/>
        <KpiCard label="Present Today"   value={fmtNum(d?.att?.present||0)}    color="var(--success)"icon="✅"/>
        <KpiCard label="Absent"          value={fmtNum(d?.att?.absent||0)}     color="var(--danger)" icon="❌"/>
        <KpiCard label="On Leave"        value={fmtNum(d?.att?.on_leave||0)}   color="var(--warning)"icon="🏖️"/>
        <KpiCard label="Pending Leaves"  value={fmtNum(d?.leaves?.length||0)}  color="var(--amber)"  icon="📅"/>
        <KpiCard label="Late Today"      value={fmtNum(d?.att?.late||0)}       color="var(--info)"   icon="⏰"/>
      </div>
      {d?.leaves?.length > 0 && (
        <div className="card" style={{ marginBottom:16 }}>
          <div className="card-header"><span className="card-title">Pending Leave Requests</span><Link to="/hr/leave" className="btn btn-outline btn-sm">Manage</Link></div>
          <div className="table-wrap">
            <table>
              <thead><tr><th>Employee</th><th>Type</th><th>From</th><th>Days</th></tr></thead>
              <tbody>
                {d.leaves.map(l => (
                  <tr key={l.request_id}>
                    <td style={{ fontWeight:500 }}>{l.emp_name}</td>
                    <td><span className="badge badge-info" style={{ fontSize:'.65rem' }}>{l.leave_type}</span></td>
                    <td className="text-sm">{fmtDate(l.from_date)}</td>
                    <td className="text-sm">{l.days}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:10 }}>
        {[['Employees','/hr/employees','👥'],['Attendance','/hr/attendance','📅'],['Payroll','/hr/payroll','💵'],['Leave','/hr/leave','🏖️']].map(([l,to,ic]) => (
          <Link key={to} to={to} style={{ textDecoration:'none' }}>
            <div className="stat-card" style={{ textAlign:'center', padding:'12px 8px', cursor:'pointer' }}>
              <div style={{ fontSize:20, marginBottom:4 }}>{ic}</div>
              <div style={{ fontSize:'.75rem', fontWeight:600, color:'var(--navy)' }}>{l}</div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

function AdminPanel({ data, loading }) {
  if (loading) return <Spinner />;
  if (!data) return null;
  const total = (data.volumes || []).reduce((s, v) => s + parseInt(v.count || 0), 0);
  const sr = data.deliverySuccessRate || 0;
  return (
    <div>
      <div className="stat-grid" style={{ marginBottom:20 }}>
        <KpiCard label="Booked Today"    value={fmtNum(total)}               color="var(--navy)"   icon="📦"/>
        <KpiCard label="Revenue MTD"     value={fmtPKR(data.revenue?.mtd)}   color="var(--success)"icon="💰"/>
        <KpiCard label="Revenue YTD"     value={fmtPKR(data.revenue?.ytd)}   color="var(--navy)"   icon="📈"/>
        <KpiCard label="Delivery Rate"   value={sr + '%'}                     color={sr>=95?'var(--success)':'var(--warning)'} icon="✅" sub="Last 30 days"/>
        <KpiCard label="Open Tickets"    value={fmtNum(data.tickets?.open_count)} color="var(--amber)"  icon="🎫"/>
        <KpiCard label="SLA Breached"    value={fmtNum(data.tickets?.breached)}   color="var(--danger)" icon="⚠️"/>
      </div>
      {data.trend?.length > 0 && (
        <div className="card" style={{ marginBottom:16 }}>
          <div className="card-header"><span className="card-title">7-Day Revenue and Volume</span></div>
          <div className="card-body">
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={data.trend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0"/>
                <XAxis dataKey="date" tick={{ fontSize:11 }} tickFormatter={d => d ? d.slice(5) : ''}/>
                <YAxis yAxisId="left" tick={{ fontSize:11 }}/>
                <YAxis yAxisId="right" orientation="right" tick={{ fontSize:11 }} tickFormatter={v => 'PKR ' + Math.round(v/1000) + 'K'}/>
                <Tooltip/>
                <Bar yAxisId="left" dataKey="total"     name="Shipments" fill="#1B2A4A" radius={[3,3,0,0]}/>
                <Bar yAxisId="left" dataKey="delivered" name="Delivered" fill="#10B981" radius={[3,3,0,0]}/>
                <Line yAxisId="right" type="monotone" dataKey="revenue" name="Revenue" stroke="#F59E0B" strokeWidth={2} dot={false}/>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(6,1fr)', gap:8 }}>
        {[['Ops','/ops','🚚'],['Finance','/finance','💰'],['HR','/hr/employees','👥'],['Customers','/admin/customers','🏢'],['Reports','/management/mis','📊'],['Admin','/admin','⚙️']].map(([l,to,ic]) => (
          <Link key={to} to={to} style={{ textDecoration:'none' }}>
            <div className="stat-card" style={{ textAlign:'center', padding:'10px 6px', cursor:'pointer' }}>
              <div style={{ fontSize:18, marginBottom:3 }}>{ic}</div>
              <div style={{ fontSize:'.7rem', fontWeight:600, color:'var(--navy)' }}>{l}</div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

// â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•
// CPY-02 NOTIFICATIONS PAGE
// â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•
export function NotificationsPage() {
  const [notifs, setNotifs]   = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/notifications?limit=50').then(r => r.data);
      if (r.success) setNotifs(r.data || []);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const markRead = async (id) => {
    await api.patch('/notifications/' + id + '/read').catch(() => {});
    setNotifs(prev => prev.map(n => (n.notif_id === id || n.log_id === id) ? { ...n, is_read:true } : n));
  };

  const markAllRead = async () => {
    await api.patch('/notifications/read-all').catch(() => {});
    setNotifs(prev => prev.map(n => ({ ...n, is_read:true })));
    toast.success('All marked as read');
  };

  const ICONS = { shipment:'📦', ticket:'🎫', finance:'💰', hr:'👥', system:'⚙️', approval:'✅' };
  const unread = notifs.filter(n => !n.is_read).length;

  return (
    <AppShell title="Notifications" actions={
      unread > 0 ? <button className="btn btn-outline btn-sm" onClick={markAllRead}>Mark all read ({unread})</button> : null
    }>
      {loading ? <Spinner /> : notifs.length ? (
        <div className="card">
          {notifs.map(n => (
            <div key={n.notif_id || n.log_id}
              style={{ padding:'12px 16px', borderBottom:'1px solid var(--gray-100)', background:n.is_read?'white':'#EFF6FF', cursor:'pointer' }}
              onClick={() => !n.is_read && markRead(n.notif_id || n.log_id)}>
              <div style={{ display:'flex', gap:12, alignItems:'flex-start' }}>
                <div style={{ fontSize:20, flexShrink:0 }}>{ICONS[n.event_type?.toLowerCase()] || '🔔'}</div>
                <div style={{ flex:1 }}>
                  <div style={{ display:'flex', justifyContent:'space-between' }}>
                    <div style={{ fontWeight:n.is_read?400:600, fontSize:'.85rem' }}>{n.subject || n.event_type?.replace(/_/g,' ')}</div>
                    <div style={{ fontSize:'.72rem', color:'var(--gray-400)' }}>{fmtDT(n.sent_at || n.created_at)}</div>
                  </div>
                  <div style={{ fontSize:'.78rem', color:'var(--gray-500)', marginTop:3 }}>{(n.body || n.recipient_contact || '').substring(0,120)}</div>
                  {!n.is_read && <span style={{ fontSize:'.62rem', background:'var(--info)', color:'white', padding:'1px 6px', borderRadius:4, marginTop:4, display:'inline-block' }}>NEW</span>}
                </div>
                <button onClick={async(e)=>{ e.stopPropagation(); await api.delete('/notifications/'+(n.notif_id||n.log_id)).catch(()=>{}); setNotifs(prev=>prev.filter(x=>(x.notif_id||x.log_id)!==(n.notif_id||n.log_id))); }}
                  style={{ background:'none', border:'none', cursor:'pointer', color:'var(--gray-300)', padding:'2px 6px', fontSize:'.7rem', flexShrink:0, alignSelf:'flex-start', marginTop:2 }}>
                  <Trash2 size={12}/>
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : <EmptyState icon="🔔" title="No notifications" />}
    </AppShell>
  );
}

// â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•
// CPY-03 APPROVALS PAGE
// â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•
export function ApprovalsPage() {
  const [items, setItems]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [tab, setTab]           = useState('PENDING');
  const [reason, setReason]     = useState('');
  const [rejectId, setRejectId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/approvals?status=' + tab + '&limit=50').then(r => r.data);
      if (r.success) setItems(r.data || []);
    } finally { setLoading(false); }
  }, [tab]);
  useEffect(() => { load(); }, [load]);

  const approve = async (id) => {
    try { await api.patch('/approvals/' + id + '/approve', {}); toast.success('Approved'); load(); }
    catch(e) { toast.error(e.response?.data?.message || 'Failed'); }
  };
  const reject = async () => {
    try { await api.patch('/approvals/' + rejectId + '/reject', { reason }); toast.success('Rejected'); setRejectId(null); setReason(''); load(); }
    catch(e) { toast.error(e.response?.data?.message || 'Failed'); }
  };

  const STATUS_BADGE = { PENDING:'badge-warning', APPROVED:'badge-success', REJECTED:'badge-danger' };

  return (
    <AppShell title="Approval Workflows">
      <div className="tabs" style={{ marginBottom:16 }}>
        {['PENDING','APPROVED','REJECTED'].map(t => (
          <div key={t} className={'tab' + (tab === t ? ' active' : '')} onClick={() => setTab(t)}>
            {t.charAt(0) + t.slice(1).toLowerCase()}
          </div>
        ))}
      </div>
      {loading ? <Spinner /> : items.length ? (
        <div>
          {tab === 'PENDING' ? items.map(item => (
            <ApprovalCard key={item.workflow_id} item={item}
              onApprove={approve} onReject={id => setRejectId(id)} />
          )) : (
            <div className="card">
              <div className="table-wrap">
                <table>
                  <thead><tr><th>Type</th><th>Requested By</th><th>Amount</th><th>Date</th><th>Status</th><th>Actioned By</th></tr></thead>
                  <tbody>
                    {items.map(item => (
                      <tr key={item.workflow_id}>
                        <td style={{ fontWeight:500 }}>{item.approval_type?.replace(/_/g,' ')}</td>
                        <td className="text-sm">{item.requester_name || '—'}</td>
                        <td>{item.requested_amount > 0 ? fmtPKR(item.requested_amount) : '—'}</td>
                        <td className="text-sm">{fmtDT(item.created_at)}</td>
                        <td><span className={'badge ' + (STATUS_BADGE[item.status] || 'badge-gray')} style={{ fontSize:'.65rem' }}>{item.status}</span></td>
                        <td className="text-sm">{item.approver_name || '—'}</td>
                        <td>{item.status==='PENDING'&&(
                          <button className="btn btn-danger btn-sm" style={{fontSize:'.7rem'}}
                            onClick={async()=>{ try{ await api.patch('/approvals/'+item.workflow_id,{ status:'CANCELLED' }); toast.success('Cancelled'); load(); }catch(e){ toast.error('Failed'); } }}>
                            <Trash2 size={11}/>
                          </button>
                        )}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      ) : <EmptyState icon="✅" title={'No ' + tab.toLowerCase() + ' approvals'} />}

      <Modal open={!!rejectId} onClose={() => { setRejectId(null); setReason(''); }} title="Reject — Enter Reason">
        <div className="modal-body">
          <div className="form-group">
            <label className="form-label">Reason</label>
            <textarea className="form-control" rows={3} placeholder="Explain why this is being rejected..."
              value={reason} onChange={e => setReason(e.target.value)} />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={() => { setRejectId(null); setReason(''); }}>Cancel</button>
          <button className="btn btn-danger" onClick={reject}>Reject</button>
        </div>
      </Modal>
    </AppShell>
  );
}

// â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•
// MAIN COMPANY PORTAL DASHBOARD (CPY-01 to CPY-05)
// â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•
export function CompanyPortalDashboard() {
  const { isAdmin, isManagement, isFinance, isHR, isOps } = useAuth();
  const navigate = useNavigate();
  const [execData, setExecData] = useState(null);
  const [opsData, setOpsData]   = useState(null);
  const [approvals, setApprovals] = useState([]);
  const [notifCount, setNotifCount] = useState(0);
  const [loading, setLoading]   = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [hubId, setHubId]       = useState('');
  const [tab, setTab]           = useState('overview');
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [exec, ops, appr, notif] = await Promise.all([
        dashboardApi.executive().catch(() => ({ success:false })),
        dashboardApi.operations().catch(() => ({ success:false })),
        api.get('/approvals?status=PENDING&limit=10').then(r=>r.data).catch(()=>({ success:false, data:[] })),
        api.get('/notifications?unread=true&limit=1').then(r=>r.data).catch(()=>({ success:false, pagination:{total:0} })),
      ]);
      if (exec.success && exec.data) setExecData(exec.data);
      if (ops.success  && ops.data)  setOpsData(ops.data);
      setApprovals(appr.success ? appr.data || [] : []);
      setNotifCount(notif.success ? notif.pagination?.total || (notif.data||[]).length : 0);
      setLastRefresh(new Date());
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    pollRef.current = setInterval(load, 60000); // CPY-04 real-time KPIs every 60s
    return () => clearInterval(pollRef.current);
  }, [load]);

  useEffect(() => {
    if (isFinance && !isAdmin && !isManagement) setTab('finance');
    else if (isHR && !isAdmin && !isManagement) setTab('hr');
    else if (isOps && !isAdmin && !isManagement && !isFinance && !isHR) setTab('ops');
    else setTab('overview');
  }, [isAdmin, isManagement, isFinance, isHR, isOps]);

  const TABS = [
    ...(isAdmin || isManagement ? [{ id:'overview', label:'Executive', icon:'📊' }] : []),
    ...(isAdmin || isOps || isManagement ? [{ id:'ops', label:'Operations', icon:'🚚' }] : []),
    ...(isAdmin || isFinance || isManagement ? [{ id:'finance', label:'Finance', icon:'💰' }] : []),
    ...(isAdmin || isHR ? [{ id:'hr', label:'HR', icon:'👥' }] : []),
  ];

  const pendingCount = approvals.length;

  return (
    <AppShell title="Company Portal" actions={
      <div style={{ display:'flex', alignItems:'center', gap:8 }}>
        <BranchSelector selected={hubId} onSelect={setHubId} />
        {lastRefresh && <span style={{ fontSize:'.72rem', color:'var(--gray-400)' }}>{lastRefresh.toLocaleTimeString()}</span>}
        <NotifBell count={notifCount} onClick={() => navigate('/notifications')} />
        <button className="btn btn-outline btn-sm" onClick={load} disabled={loading}><RefreshCw size={14}/></button>
      </div>
    }>
      {/* Pending approvals banner (CPY-03) */}
      {pendingCount > 0 && (
        <div style={{ padding:'10px 16px', background:'#FFFBEB', border:'1px solid #FDE68A', borderRadius:8, marginBottom:16, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <div style={{ display:'flex', gap:10, alignItems:'center' }}>
            <Clock size={15} style={{ color:'var(--amber)' }}/>
            <span style={{ fontWeight:600, fontSize:'.85rem', color:'var(--amber)' }}>
              {pendingCount} approval{pendingCount > 1 ? 's' : ''} waiting for your action
            </span>
          </div>
          <Link to="/approvals" className="btn btn-amber btn-sm">Review Now</Link>
        </div>
      )}

      {/* Role tabs (CPY-01) */}
      {TABS.length > 1 && (
        <div className="tabs" style={{ marginBottom:20 }}>
          {TABS.map(t => (
            <div key={t.id} className={'tab' + (tab === t.id ? ' active' : '')} onClick={() => setTab(t.id)}>
              <span style={{ marginRight:5 }}>{t.icon}</span>{t.label}
            </div>
          ))}
        </div>
      )}

      {tab === 'overview' && <AdminPanel  data={execData} loading={loading && !execData} />}
      {tab === 'ops'      && <OpsPanel    data={opsData}  loading={loading && !opsData} hubId={hubId} />}
      {tab === 'finance'  && <FinancePanel />}
      {tab === 'hr'       && <HRPanel />}
    </AppShell>
  );
}

// â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•
// CPY-05 MULTI-BRANCH KPI PAGE
// â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•
export function MultiBranchKpiPage() {
  const [hubs, setHubs]   = useState([]);
  const [kpis, setKpis]   = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get('/admin/hubs').then(r => r.data).catch(() => ({ success:false, data:[] })),
      api.get('/dashboard/branch-kpis').then(r => r.data).catch(() => ({ success:false, data:[] })),
    ]).then(([h, k]) => {
      setHubs(h.success ? h.data : []);
      setKpis(k.success ? k.data : []);
    }).finally(() => setLoading(false));
  }, []);

  const getKpi = (hubId) => kpis.find(k => k.hub_id === hubId) || {};

  return (
    <AppShell title="Multi-Branch KPIs">
      {loading ? <Spinner /> : hubs.length ? (
        <div style={{ display:'grid', gap:12 }}>
          {hubs.map(hub => {
            const k = getKpi(hub.hub_id);
            return (
              <div key={hub.hub_id} className="card">
                <div className="card-body">
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
                    <div>
                      <div style={{ fontWeight:700 }}>{hub.name}</div>
                      <div style={{ fontSize:'.72rem', color:'var(--gray-400)' }}>{hub.type} · {hub.city} · {hub.country}</div>
                    </div>
                    <span style={{ padding:'3px 10px', borderRadius:10, fontSize:'.7rem', fontWeight:600, background:hub.is_active?'#DCFCE7':'#F3F4F6', color:hub.is_active?'#166534':'#6B7280' }}>
                      {hub.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <div style={{ display:'grid', gridTemplateColumns:'repeat(5,1fr)', gap:8 }}>
                    {[['Booked Today',k.booked_today||0,'var(--navy)'],['Delivered',k.delivered_today||0,'var(--success)'],['At Hub',k.at_hub||0,'var(--info)'],['Pending',k.pending||0,'var(--amber)'],['Success Rate',(k.delivery_rate||0)+'%',k.delivery_rate>=95?'var(--success)':'var(--warning)']].map(([l,v,c]) => (
                      <div key={l} style={{ textAlign:'center', padding:'8px', borderRadius:6, background:'var(--gray-50)' }}>
                        <div style={{ fontSize:'.65rem', color:'var(--gray-400)', marginBottom:3 }}>{l}</div>
                        <div style={{ fontWeight:700, fontSize:'.9rem', color:c }}>{v}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : <EmptyState icon="🏢" title="No branches configured" message={<Link to="/admin/hubs" className="btn btn-primary btn-sm">Add Hubs</Link>} />}
    </AppShell>
  );
}
